/**
 * Shared transient network error detection.
 * Used by React Query (retry logic) and Sentry (beforeSend filter).
 *
 * RETIRE-WHEN (Great Migration N-2): `isJWTClockSkewError` (PGRST303) and its
 * consumer `asyncRetryHandler` in src/App.tsx exist only because PostgREST
 * rejects a clock-skewed JWT. Delete them — globally, in one PR — when the LAST
 * direct PostgREST read is gone (`tools/ci/authz-split-baseline.json` total ==
 * 0). The generic transient-network patterns below are transport-agnostic and
 * stay.
 */

const TRANSIENT_NETWORK_PATTERNS = [
  'failed to fetch',
  'network request failed',
  'networkerror',
  'network error',
  'fetch failed',
  'load failed',
  'connection refused',
  'connection reset',
  'timeout',
  'timed out',
  'offline',
  'no internet',
  'unreachable',
  'aborterror',
  'signal is aborted',
  'request aborted',
  'aborted',
  // HTTP 5xx gateway/server errors (transient infrastructure issues)
  'status code: 502',
  'status code: 503',
  'status code: 504',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
  // Kong/nginx upstream proxy errors — backend sent a malformed HTTP response
  // (e.g. during a PM2 restart or API deploy). Same semantics as a 502; the
  // string differs because it originates from the proxy layer, not the browser.
  'invalid response was received from the upstream',
  'upstream connect error or disconnect/reset before headers',
  'no healthy upstream',
  // JWT clock skew errors (user device clock ahead of server)
  'jwt issued at future',
  'pgrst303',
  // Supabase auth-js retryable fetch failure in name-prefixed string form
  // ("AuthRetryableFetchError: {}"), as produced by String(err) /
  // console-capture serialization. NOTE: a Sentry exception.value typically
  // carries only the bare message ("{}") without the class name — that path
  // is covered by the exceptionType check in sentry.ts's beforeSend, not by
  // this pattern. See the name-based check below for why the class name, not
  // the message, is the only reliable signal.
  'authretryablefetcherror',
];

/**
 * Every typed error class of @supabase/auth-js (2.95.3), classified noise-vs-
 * signal ONCE. Two of these already burned a Sentry-alert → GitHub-issue →
 * triage → fix cycle each because a message-pattern filter is structurally
 * blind to typed SDK errors — the class NAME is stable and load-bearing while
 * the message is dynamic or empty ("{}"): AuthApiError (#906), then
 * AuthRetryableFetchError (#3575). This list closes the class instead of
 * waiting for the next instance to page us.
 *
 * NOISE (self-healing; the auth state listener / UI already handles the
 * outcome — Sentry beforeSend drops these by name):
 *   - AuthApiError            GoTrue rejected with a structured status
 *                             (expired/invalid session, wrong OTP). Surfaced
 *                             by the UI; the auth listener heals or signs out.
 *   - AuthRetryableFetchError network-layer failure reaching GoTrue —
 *                             retryable by definition, auth-js retries it.
 *   - AuthSessionMissingError getUser()/refresh raced a signed-out session
 *                             ("Auth session missing!"). The auth listener
 *                             redirects to login; nothing is broken.
 *
 * SIGNAL (must keep reaching Sentry — each one firing means breakage):
 *   - AuthError / CustomAuthError     base classes — never filter a hierarchy
 *   - AuthUnknownError                non-JSON GoTrue response — infra breakage
 *   - AuthInvalidTokenResponseError   malformed token endpoint response
 *   - AuthInvalidCredentialsError     thrown for wrong CLIENT API usage
 *                                     (missing phone/email arg) — a code bug,
 *                                     not a user-input rejection
 *   - AuthImplicitGrantRedirectError / AuthPKCEGrantCodeExchangeError /
 *     AuthPKCECodeVerifierMissingError  OAuth/PKCE flows this app never runs
 *   - AuthWeakPasswordError           password auth unused (OTP-only)
 *   - AuthInvalidJwtError             local JWT validation failed — during the
 *                                     GoTrue-off migration (D4) a bad signing
 *                                     key is exactly what this would catch
 *
 * networkErrors.test.ts enumerates the Auth* error classes exported by the
 * installed SDK and fails on any class absent from both lists above — an
 * auth-js upgrade that introduces a new error class must classify it here
 * before it can merge.
 *
 * Matched by NAME (not instanceof) so consumers don't import
 * @supabase/auth-js and duplicate bundle chunks can't split class identity.
 */
export const AUTH_JS_NOISE_ERROR_NAMES = [
  'AuthApiError',
  'AuthRetryableFetchError',
  'AuthSessionMissingError',
] as const;

export function isTransientNetworkError(error: unknown): boolean {
  // Supabase auth-js throws AuthRetryableFetchError ONLY when the fetch to
  // GoTrue failed at the network layer or returned a retryable 5xx. Its
  // message is frequently an empty serialized object ("{}"; Sentry #3575), so
  // no message pattern can match it — the class NAME is the signal. Matched by
  // name (not instanceof) so this file doesn't import @supabase/auth-js and
  // duplicate bundle chunks can't split the class identity.
  if (error instanceof Error && error.name === 'AuthRetryableFetchError') return true;
  const errorMessage = error instanceof Error ? error.message : String(error);
  const normalizedMessage = errorMessage.toLowerCase();
  return TRANSIENT_NETWORK_PATTERNS.some(pattern => normalizedMessage.includes(pattern));
}

/**
 * Detects PostgreSQL constraint violation errors (user input errors, not bugs).
 * 23505 = unique_violation, 23503 = foreign_key_violation, 23514 = check_violation
 */
export function isConstraintViolationError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    const code = (error as Record<string, unknown>).code;
    if (code === '23505' || code === '23503' || code === '23514') {
      return true;
    }
  }
  return false;
}

/**
 * Detects a NestRequestError that represents an expired or missing session —
 * both are self-healing and should not be reported to Sentry.
 *
 * Two failure modes are matched:
 *  - kind === "auth": nestRequest.ts couldn't find a Supabase session on the
 *    frontend (getSession returned null). Transient race between GoTrue clearing
 *    a session and React propagating the resulting null companyId.
 *  - kind === "http" && status === 401: the NestJS API received the token but
 *    GoTrue (or local HS256) rejected it as "Invalid or expired token". This
 *    happens when the Supabase JWT expires in-flight while the client still has
 *    the old token cached. Also self-healing — the Supabase client refreshes
 *    the session and subsequent requests succeed.
 *
 * Nest-only repositories (brokerageContracts, teams, leads) have no Supabase
 * fallback, so the 401 propagates directly to React Query's QueryCache.onError.
 * Without this guard, it reaches Sentry.captureException (Sentry issue
 * DALLALI-FRONTEND-DG / #3076).
 *
 * Duck-typed intentionally to avoid importing nestRequest.ts here.
 */
export function isNestAuthError(error: unknown): boolean {
  if (!(error instanceof Error) || error.name !== 'NestRequestError') return false;
  const e = error as { kind?: string; status?: number };
  if (e.kind === 'auth') return true;
  if (e.kind === 'http' && e.status === 401) return true;
  return false;
}

/**
 * Detects a NestRequestError carrying a 4xx response — an expected domain /
 * validation rejection (duplicate identity, bad input, forbidden action) that
 * the UI already surfaces via toast/form error. These must never go through
 * console.error: captureConsoleIntegration({ levels: ['error'] }) turns every
 * console.error into a Sentry event, producing false alarms whose server
 * messages can carry PII (Sentry issue DALLALI-FRONTEND-DF / #3075).
 *
 * Includes 401 auth errors by design — an expired-JWT rejection mid-submit is
 * also expected and self-healing (see isNestAuthError above). Callers that
 * need only domain rejections (409 duplicate, 400 validation) should combine
 * with !isNestAuthError(error). Conversely, kind=auth (no session found
 * client-side) is NOT matched here — callers wanting full suppression of
 * every expected error should check isNestAuthError(error) too (its message
 * is generic, so nothing sensitive leaks either way).
 * Duck-typed intentionally to avoid importing nestRequest.ts here.
 */
export function isExpectedNestClientError(error: unknown): boolean {
  if (!(error instanceof Error) || error.name !== 'NestRequestError') return false;
  const e = error as { kind?: string; status?: number };
  return e.kind === 'http' && typeof e.status === 'number' && e.status >= 400 && e.status < 500;
}

/**
 * Detects a CODED Nest domain rejection: a NestRequestError carrying a 4xx
 * status AND a machine code from the API's bilingual AppError envelope
 * (conventions §3 — e.g. `accounting-002` "no connection for this company",
 * `accounting-003` "provider test failed"). The code's presence proves the
 * server refused deliberately with a user-facing ar/en message the UI already
 * toasts — an expected business state, not breakage. Capturing the throw as an
 * exception produced the Arabic-titled noise classes on the super-admin
 * accounting page (Sentry DALLALI-FRONTEND-GZ / #3659).
 *
 * Deliberately narrower than isExpectedNestClientError: an UNCODED 4xx (a 404
 * from a mis-routed endpoint, a bare 400) still reaches Sentry as an
 * exception, and 401 is excluded — the auth-race guard (isNestAuthError) owns
 * that class and must keep running its session heal. Callers should downgrade
 * these to a fingerprinted warning (not silently drop them) so a spike in any
 * one rejection code stays observable.
 */
export function isCodedNestDomainError(error: unknown): boolean {
  if (!(error instanceof Error) || error.name !== 'NestRequestError') return false;
  const e = error as { kind?: string; status?: number; code?: unknown };
  return (
    e.kind === 'http' &&
    typeof e.status === 'number' &&
    e.status >= 400 &&
    e.status < 500 &&
    e.status !== 401 &&
    typeof e.code === 'string' &&
    e.code.length > 0
  );
}

/**
 * Detects an EXPECTED Postgres permission denial (42501) surfaced through
 * PostgREST — a guarded RPC's `RAISE EXCEPTION 'not authorized…' USING ERRCODE
 * = 'insufficient_privilege'` (the master-card guard pattern), a table-level
 * grant denial on a plain `.from()` read (e.g. an `anon`-role query after
 * session loss — see sessionHeal.ts), or the legacy guards that raise a bare
 * `'not authorized'` without an ERRCODE (surfaced as P0001). The "Rpc" in the
 * name is historical — it matches the `rpc-permission-denial` Sentry group
 * vocabulary, but the code keys on the error code, not on how the query was
 * issued. These are authorization boundaries working as designed: the user
 * sees a correct "not authorized" toast, nothing is broken, and reporting them
 * through captureException produced the uncategorised `Error: not authorized`
 * noise class (Sentry DALLALI-FRONTEND-F4 / #3414).
 *
 * Deliberately narrow — keyed on the Postgres error code, never on the raw
 * message alone — so a genuine wrongful-denial regression in another error
 * shape still reaches Sentry as an exception. Callers should downgrade these
 * to a fingerprinted warning (not silently drop them) so a spike in denials
 * for legitimate users stays observable.
 */
export function isRpcPermissionDenialError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;
  if (e.code === '42501') return true;
  // LEGACY-ONLY escape hatch: the pre-ERRCODE guards (hatif credentials,
  // notification config) raise a bare 'not authorized' → PostgREST P0001. The
  // anchored prefix match exists solely for them. Any NEW guard must raise
  // `USING ERRCODE = 'insufficient_privilege'` and go through the 42501 branch
  // above — do not lean on this P0001 match for new code.
  if (e.code === 'P0001' && typeof e.message === 'string' && /^not authorized\b/i.test(e.message.trim())) {
    return true;
  }
  return false;
}

/**
 * An EXPECTED business-rule rejection from a guarded RPC envelope — the RPC
 * returned `{ success: false, error_code: … }` as designed (e.g.
 * `target_is_super_admin` when demoting a super admin, `last_super_admin`,
 * `no_self_revoke`) and the caller throws so React Query's onError can toast
 * the bilingual message. The UX is correct; only the Sentry capture of the
 * throw was noise (Sentry DALLALI-FRONTEND-FV / #3472). The MutationCache
 * guard in App.tsx suppresses this class — same family as the
 * isRpcPermissionDenialError (#3414) and isConstraintViolationError guards.
 *
 * Distinct from isRpcPermissionDenialError: that matches Postgres-level
 * denials (42501/P0001) raised inside the function; this matches
 * application-level refusals delivered in a success:false envelope, where the
 * only "error" object in existence is the one the frontend itself constructs.
 */
export class ExpectedRpcRejectionError extends Error {
  readonly errorCode: string;

  constructor(message: string, errorCode: string) {
    super(message);
    this.name = 'ExpectedRpcRejectionError';
    this.errorCode = errorCode;
  }
}

/** Matched by name (not instanceof) so duplicate bundle chunks can't split the class identity. */
export function isExpectedRpcRejectionError(error: unknown): boolean {
  return error instanceof Error && error.name === 'ExpectedRpcRejectionError';
}

/**
 * Detects a Hatif (Voxa) upstream-dependency failure surfaced through our API:
 * the NestJS SentryExceptionFilter maps HatifApiError to 424 (upstream
 * config/credential fault, e.g. an expired WABA token) or 502/503/504 (upstream
 * outage or gateway failure). The UI already degrades — e.g. the notification-
 * templates page falls back to seeded copy — so capturing the throw as an
 * exception produced the `Request failed with status code 500` noise class
 * (Sentry DALLALI-FRONTEND-GF / #3593, DALLALI-NESTJS-API-1M / #3592).
 *
 * A 404 is matched ONLY when it carries the API's `hatif-*` machine code —
 * i.e. it came through the HatifApiError mapping (template/WABA genuinely
 * absent upstream, a state the UI already degrades for). A 404 without the
 * code is our own routing/endpoint bug and stays reportable. 429 stays
 * reportable too: a Meta rate-limit burst is a capacity signal worth an
 * exception, and React Query's back-off owns the retry.
 *
 * Deliberately narrow — keyed on the typed HatifRequestError's status, never
 * on message text — so a genuine 4xx/5xx bug in OUR hatif endpoints (400
 * validation, our own 500) still reaches Sentry as an exception. Callers
 * should downgrade these to a fingerprinted warning (not silently drop them)
 * so a sustained provider outage stays observable.
 *
 * Matched by name (not instanceof) so duplicate bundle chunks can't split the
 * class identity.
 */
export function isHatifUpstreamError(error: unknown): boolean {
  if (!(error instanceof Error) || error.name !== 'HatifRequestError') return false;
  const { status, code } = error as { status?: unknown; code?: unknown };
  if (typeof status !== 'number') return false;
  if (status === 424 || status === 502 || status === 503 || status === 504) return true;
  return status === 404 && typeof code === 'string' && code.startsWith('hatif-');
}

export function isJWTClockSkewError(error: unknown): boolean {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const normalizedMessage = errorMessage.toLowerCase();

  // Check for PGRST303 code or "JWT issued at future" message
  if (normalizedMessage.includes('pgrst303') || normalizedMessage.includes('jwt issued at future')) {
    return true;
  }

  // Also check structured Supabase errors
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;
    if (errorObj.code === 'PGRST303' || errorObj.code === 'pgrst303') {
      return true;
    }
    if (errorObj.message && String(errorObj.message).toLowerCase().includes('jwt issued at future')) {
      return true;
    }
  }

  return false;
}
